import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { EUserRole} from '@/types';
import { ROLE_PORTAL_MAP, type Portal, type User } from '@/types';
import { resetQueryCache } from '@/lib/offline/query-persist';

export type KYBStatus = 'NOT_SUBMITTED' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | null;

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  activeRole: EUserRole | null;
  activePortal: Portal | null;
  /**
   * Effective permission names for the current user (`resource.action`).
   * `['*']` means super admin (all permissions). Kept in sync with the latest
   * `/auth/get-profile` response so permission changes propagate to the UI.
   */
  permissions: string[];
  kybStatus: KYBStatus;
  mustChangePassword: boolean;
  mustCompleteMineSetup: boolean;
  mustCompleteInspectorProfile: boolean;
  lastLogoutReason: string | null;
  /**
   * Monotonic counter bumped on every `login` and `logout`. In-memory only
   * (never persisted) so it resets to 0 on each page load. The API client uses
   * it to discard a token-refresh whose session ended mid-flight, preventing a
   * previous user's refreshed token from clobbering the current session.
   */
  sessionId: number;

  setUser: (user: User) => void;
  setPermissions: (permissions: string[]) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (
    user: User,
    accessToken: string,
    refreshToken: string,
    kybStatus?: KYBStatus,
    mustChangePassword?: boolean,
    mustCompleteMineSetup?: boolean,
    mustCompleteInspectorProfile?: boolean,
  ) => void;
  logout: (options?: { reason?: string }) => void;
  updateUser: (updates: Partial<User>) => void;
  switchRole: (role: EUserRole) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      activeRole: null,
      activePortal: null,
      permissions: [],
      kybStatus: null,
      mustChangePassword: false,
      lastLogoutReason: null,
      mustCompleteMineSetup: false,
      mustCompleteInspectorProfile: false,
      sessionId: 0,

      setUser: (user) =>
        set({ user, permissions: user.permissions ?? [] }),

      setPermissions: (permissions) => set({ permissions }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      login: (
        user,
        accessToken,
        refreshToken,
        kybStatus?,
        mustChangePassword?,
        mustCompleteMineSetup?,
        mustCompleteInspectorProfile?,
      ) => {
        const activeRole = user.roles[0] ?? user.role;
        const activePortal = ROLE_PORTAL_MAP[activeRole];
        set((state) => ({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          activeRole,
          activePortal,
          permissions: user.permissions ?? [],
          kybStatus: kybStatus ?? null,
          mustChangePassword: mustChangePassword ?? false,
          mustCompleteMineSetup: mustCompleteMineSetup ?? false,
          mustCompleteInspectorProfile: mustCompleteInspectorProfile ?? false,
          // Start a new session generation so any in-flight refresh from the
          // previous session is discarded instead of overwriting these tokens.
          sessionId: state.sessionId + 1,
        }));
      },

      logout: (options) => {
        // Drop the previous session's cached query data so a subsequent
        // login can't inherit stale layouts / per-role responses. Best-effort
        // — never block the auth state transition on this.
        void resetQueryCache();
        // Belt-and-braces: wipe the persisted blob synchronously so a delayed
        // persist write can't resurrect the previous user's tokens.
        try {
          localStorage.removeItem('minetech-auth');
        } catch {
          // ignore — persist middleware still clears in-memory state below
        }
        set((state) => ({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          activeRole: null,
          activePortal: null,
          permissions: [],
          kybStatus: null,
          mustChangePassword: false,
          mustCompleteMineSetup: false,
          mustCompleteInspectorProfile: false,
          lastLogoutReason: options?.reason ?? null,
          // Invalidate the session generation so any in-flight refresh aborts.
          sessionId: state.sessionId + 1,
        }));
      },

      updateUser: (updates) =>
        set((state) => ({ user: state.user ? { ...state.user, ...updates } : null })),

      switchRole: (role) => {
        const user = get().user;
        if (!user?.roles.includes(role)) return;
        set({ activeRole: role, activePortal: ROLE_PORTAL_MAP[role] });
      },
    }),
    {
      name: 'minetech-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeRole: state.activeRole,
        activePortal: state.activePortal,
        permissions: state.permissions,
        kybStatus: state.kybStatus,
        mustChangePassword: state.mustChangePassword,
        lastLogoutReason: state.lastLogoutReason,
        mustCompleteMineSetup: state.mustCompleteMineSetup,
        mustCompleteInspectorProfile: state.mustCompleteInspectorProfile,
      }),
    },
  ),
);
